import styled from "styled-components";
import {Colors} from "../Styles/Colors";
import {useNavigate} from "react-router-dom";
import {ComponentType} from "react";
import {IconBaseProps} from "react-icons";
import {GrWorkshop} from "react-icons/gr";
import {SiArtstation} from "react-icons/si";
import {GiCargoCrate, GiMining, GiTrade} from "react-icons/gi";
import {MdOutlineArrowForwardIos} from "react-icons/md";
import useIndustrialTasks from "../Provider/useIndustrialTasks";

type IndustrialNavigationItem = {
	title: string;
	subtitle: string;
	description: string;
	path: string;
	icon: ComponentType<IconBaseProps>;
};

const Styles = {
	Page: styled.div`
		padding: 28px;
		color: ${Colors.raw.terminal_light};
		text-align: left;
	`,

	Header: styled.div`
		display: grid;
		gap: 12px;
		margin-bottom: 28px;
	`,

	Eyebrow: styled.div`
		color: ${Colors.raw.neon_orange};
		letter-spacing: 3px;
		text-transform: uppercase;
		font-size: 12px;
	`,

	Title: styled.h1`
		margin: 0;
		font-size: clamp(32px, 6vw, 58px);
		line-height: 1;
		text-transform: uppercase;
		text-shadow: 0 0 18px rgba(255, 122, 0, 0.45);
	`,

	Subline: styled.div`
		max-width: 820px;
		opacity: 0.72;
		font-size: 14px;
		line-height: 1.55;
	`,

	Overview: styled.div`
		display: grid;
		grid-template-columns: 1.4fr 0.8fr;
		gap: 18px;
		margin-bottom: 26px;

		@media (max-width: 920px) {
			grid-template-columns: 1fr;
		}
	`,

	HeroPanel: styled.section`
		position: relative;
		overflow: hidden;
		min-height: 210px;
		padding: 24px;
		border: 1px solid rgba(255, 153, 51, 0.38);
		background:
			radial-gradient(circle at top right, rgba(255, 122, 0, 0.24), transparent 36%),
			linear-gradient(135deg, rgba(255, 255, 255, 0.065), rgba(255, 255, 255, 0.018));
		box-shadow: 0 0 24px rgba(255, 122, 0, 0.12);
		clip-path: polygon(0 0, calc(100% - 28px) 0, 100% 28px, 100% 100%, 0 100%);

		&::before {
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			height: 3px;
			width: 100%;
			background: ${Colors.raw.neon_orange};
		}
	`,

	HeroIcon: styled(GrWorkshop as ComponentType<IconBaseProps>)`
		position: absolute;
		right: 24px;
		bottom: 18px;
		width: 120px;
		height: 120px;
		color: rgba(255, 122, 0, 0.16);
	`,

	HeroTitle: styled.h2`
		position: relative;
		margin: 0 0 12px 0;
		color: ${Colors.raw.terminal_light};
		font-size: 26px;
		text-transform: uppercase;
		letter-spacing: 1px;
	`,

	HeroText: styled.p`
		position: relative;
		max-width: 640px;
		margin: 0;
		opacity: 0.76;
		font-size: 14px;
		line-height: 1.55;
	`,

	StatusPanel: styled.section`
		display: grid;
		gap: 10px;
	`,

	StatusBox: styled.div`
		border: 1px solid rgba(255, 153, 51, 0.32);
		background: rgba(0, 0, 0, 0.26);
		padding: 15px;
		box-shadow: 0 0 16px rgba(255, 122, 0, 0.08);
	`,

	StatusLabel: styled.div`
		opacity: 0.56;
		font-size: 10px;
		text-transform: uppercase;
		letter-spacing: 1.6px;
		margin-bottom: 6px;
	`,

	StatusValue: styled.div`
		color: ${Colors.raw.neon_orange};
		font-family: "Courier New", monospace;
		font-size: 17px;
		font-weight: 800;
	`,

	SectionTitle: styled.div`
		display: flex;
		align-items: center;
		gap: 10px;
		margin: 10px 0 16px 0;
		color: ${Colors.raw.neon_orange};
		text-transform: uppercase;
		letter-spacing: 2px;
		font-size: 12px;

		&::after {
			content: "";
			height: 1px;
			flex: 1;
			background: rgba(255, 153, 51, 0.28);
		}
	`,

	Grid: styled.div`
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
		gap: 18px;
	`,

	NavigationCard: styled.button<{ $disabled?: boolean }>`
		position: relative;
		overflow: hidden;
		display: grid;
		grid-template-columns: auto 1fr auto;
		gap: 16px;
		align-items: center;
		width: 100%;
		min-height: 138px;
		padding: 18px;
		text-align: left;
		border: 1px solid ${({ $disabled }) =>
		$disabled ? "rgba(255, 255, 255, 0.14)" : "rgba(255, 153, 51, 0.42)"};
		background: ${({ $disabled }) =>
		$disabled
			? "rgba(255, 255, 255, 0.025)"
			: "linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.018))"};
		color: ${Colors.raw.terminal_light};
		cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
		opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
		box-shadow: ${({ $disabled }) =>
		$disabled ? "none" : "0 0 20px rgba(255, 122, 0, 0.10)"};
		clip-path: polygon(0 0, calc(100% - 22px) 0, 100% 22px, 100% 100%, 0 100%);

		&::before {
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			height: 3px;
			width: 100%;
			background: ${({ $disabled }) =>
		$disabled ? "rgba(255, 255, 255, 0.16)" : Colors.raw.neon_orange};
		}

		&:hover {
			border-color: ${({ $disabled }) =>
		$disabled ? "rgba(255, 255, 255, 0.14)" : Colors.raw.neon_orange};
			box-shadow: ${({ $disabled }) =>
		$disabled ? "none" : "0 0 22px rgba(255, 122, 0, 0.20)"};
			transform: ${({ $disabled }) => ($disabled ? "none" : "translateY(-2px)")};
		}
	`,

	CardIconBox: styled.div<{ $disabled?: boolean }>`
		width: 54px;
		height: 54px;
		display: grid;
		place-items: center;
		border: 1px solid ${({ $disabled }) =>
		$disabled ? "rgba(255, 255, 255, 0.16)" : "rgba(255, 153, 51, 0.48)"};
		background: ${({ $disabled }) =>
		$disabled ? "rgba(255, 255, 255, 0.04)" : "rgba(255, 122, 0, 0.12)"};
		color: ${({ $disabled }) =>
		$disabled ? "rgba(255, 255, 255, 0.45)" : Colors.raw.neon_orange};

		svg {
			width: 30px;
			height: 30px;
		}
	`,

	CardContent: styled.div`
		display: grid;
		gap: 6px;
		min-width: 0;
	`,

	CardTitleRow: styled.div`
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;
	`,

	CardTitle: styled.div`
		font-size: 19px;
		font-weight: 800;
		text-transform: uppercase;
		letter-spacing: 1px;
	`,

	CardSubtitle: styled.div`
		color: ${Colors.raw.neon_orange};
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 1.6px;
	`,

	CardDescription: styled.div`
		opacity: 0.66;
		font-size: 13px;
		line-height: 1.4;
	`,

	StatusBadge: styled.span<{ $status: string }>`
		border: 1px solid ${({ $status }) =>
		$status === "online"
			? "rgba(67, 255, 157, 0.45)"
			: $status === "soon"
				? "rgba(255, 153, 51, 0.45)"
				: "rgba(255, 255, 255, 0.18)"};
		background: ${({ $status }) =>
		$status === "online"
			? "rgba(67, 255, 157, 0.10)"
			: $status === "soon"
				? "rgba(255, 122, 0, 0.10)"
				: "rgba(255, 255, 255, 0.04)"};
		color: ${({ $status }) =>
		$status === "online"
			? "#8dffc5"
			: $status === "soon"
				? Colors.raw.neon_orange
				: "rgba(255, 255, 255, 0.55)"};
		padding: 4px 7px;
		font-size: 10px;
		font-weight: 800;
		text-transform: uppercase;
		letter-spacing: 1px;
	`,

	Arrow: styled(MdOutlineArrowForwardIos as ComponentType<IconBaseProps>)<{ $disabled?: boolean }>`
		width: 24px;
		height: 24px;
		color: ${({ $disabled }) =>
		$disabled ? "rgba(255, 255, 255, 0.22)" : Colors.raw.neon_orange};
	`,
};

const navigationItems: IndustrialNavigationItem[] = [
	{
		title: "Standorte",
		subtitle: "Refinery & Terminal Network",
		description:
			"Alle Raffinerie- und Industrie-Terminals mit Kapazitäten, Systemen, Planeten und Stationen.",
		path: "/industrial-jobs/locations",
		icon: SiArtstation,
	},
	{
		title: "Sondieren",
		subtitle: "Neue Aufträge erstellen",
		description:
			"Materialen werden sondiert und Unreinheiten industiel entfernt.",
		path: "/industrial-jobs/create",
		icon: GiMining
	},
	{
		title: "Aufträge",
		subtitle: "Waren Abholung",
		description:
			"Aufträge wurden abgeschlossen. Diese können auf den jeweiligen Orten abgeholt werden.",
		path: "/industrial-jobs/my-jobs",
		icon: GiCargoCrate
	},
];

const NavigationButton = ({item}: { item: IndustrialNavigationItem }) => {

	const navigate = useNavigate();
	const Icon = item.icon;
	// const disabled = item.status !== "online";
	const disabled = false;

	const handleClick = () => {
		if (disabled) {
			return;
		}

		void navigate(item.path);
	};

	return (
		<Styles.NavigationCard
			type="button"
			$disabled={disabled}
			onClick={handleClick}
		>
			<Styles.CardIconBox $disabled={disabled}>
				<Icon />
			</Styles.CardIconBox>

			<Styles.CardContent>
				<Styles.CardTitleRow>
					<Styles.CardTitle>{item.title}</Styles.CardTitle>
				</Styles.CardTitleRow>

				<Styles.CardSubtitle>{item.subtitle}</Styles.CardSubtitle>
				<Styles.CardDescription>{item.description}</Styles.CardDescription>
			</Styles.CardContent>

			<Styles.Arrow $disabled={disabled} />
		</Styles.NavigationCard>
	);
};

export const IndustrialJobs = () => {

	const { inProgress, completedTasks } = useIndustrialTasks();

	return (
		<Styles.Page>
			<Styles.Header>
				<Styles.Eyebrow>Industrial Operations</Styles.Eyebrow>
				<Styles.Title>Industrie</Styles.Title>
				<Styles.Subline>
					Zentrale für industrielle Operationen im Verse. Wähle einen Bereich aus,
					um in die jeweilige Unterseite zu navigieren.
				</Styles.Subline>
			</Styles.Header>

			<Styles.Overview>
				<Styles.HeroPanel>
					<Styles.HeroIcon />
					<Styles.HeroTitle>Operations-Konsole</Styles.HeroTitle>
					<Styles.HeroText>
						Plane Raffinerie-Läufe, prüfe industrielle Standorte und bereite
						künftige Mining-, Cargo- und Handelsoperationen vor. Die
						Industrie-Zentrale dient als Einstiegspunkt für alle Unterseiten.
					</Styles.HeroText>
				</Styles.HeroPanel>

				<Styles.StatusPanel>
					<Styles.StatusBox>
						<Styles.StatusLabel>Aktive Aufträge</Styles.StatusLabel>
						<Styles.StatusValue>{ inProgress.length }</Styles.StatusValue>
					</Styles.StatusBox>

					<Styles.StatusBox>
						<Styles.StatusLabel>Abgeschlossene Aufträge</Styles.StatusLabel>
						<Styles.StatusValue>{ completedTasks.length }</Styles.StatusValue>
					</Styles.StatusBox>

					<Styles.StatusBox>
						<Styles.StatusLabel>nächster Auftrag wird abgeschlossen in</Styles.StatusLabel>
						<Styles.StatusValue>{
							inProgress
								.sort((a, b) =>
									(a.timestamp.getDate() + a.durationInMinutes) -(b.timestamp.getDate() + b.durationInMinutes))
							?.[0]?.location ?? "--"
						}</Styles.StatusValue>
					</Styles.StatusBox>
				</Styles.StatusPanel>
			</Styles.Overview>

			<Styles.SectionTitle>Navigation</Styles.SectionTitle>

			<Styles.Grid>
				{navigationItems.map(item => (
					<NavigationButton key={item.path} item={item} />
				))}
			</Styles.Grid>
		</Styles.Page>
	);
};